<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import { ButtonType, CustomButton } from '@/components/common/';
import type { CaseModel } from '@/models';
import { useStudyStore } from '@/stores/study';
import { useCaseNoteStore } from '@/stores/caseNote';
import CaseNoteDialog from './CaseNoteDialog.vue';
import CaseNotesPanel from './CaseNotesPanel.vue';
import { getPumpTimepointOptions } from './caseNoteTimepoints';

const props = defineProps({
  caseModel: {
    type: Object as PropType<CaseModel>,
    required: true
  },
  showText: {
    type: Boolean,
    default: false
  },
  onSidebar: {
    type: Boolean,
    default: false
  }
});

const showNoteDialog = ref(false);
const showNotesPanel = ref(false);
const studyStore = useStudyStore();
const caseNoteStore = useCaseNoteStore();
const localCaseNoteCount = ref(props.caseModel.caseNoteCount ?? 0);

watch(
  () => props.caseModel.caseNoteCount,
  newCount => {
    localCaseNoteCount.value = newCount ?? 0;
  }
);

const pumpTimepointOptions = computed(
  () => getPumpTimepointOptions(props.caseModel, studyStore.selectedStudy)
);

const emit = defineEmits<{
  onPinChange: [pinned: boolean];
}>();

const togglePanel = (event: MouseEvent) => {
  event.stopPropagation();

  const redirectedToPinnedPanel = caseNoteStore.requestPinnedPanelCase(
    props.caseModel.identifier,
    props.caseModel.label
  );
  if (redirectedToPinnedPanel) {
    return;
  }

  showNotesPanel.value = true;
};

const handleNoteSaved = async () => {
  showNoteDialog.value = false;
  if (showNotesPanel.value) {
    await caseNoteStore.getNotes(props.caseModel.identifier);
    localCaseNoteCount.value = caseNoteStore.notes.length;
    return;
  }

  localCaseNoteCount.value += 1;
};
</script>

<template>
  <div class="note-button-root"
    :class="{ 'has-notes': localCaseNoteCount > 0, 'on-sidebar': onSidebar }"
    @click.stop>
    <div class="note-btn-wrapper">
      <CustomButton :icon="{ icon: 'note-sticky', style: 'fal' }"
        :type="ButtonType.ICON_ONLY"
        tooltip="Case notes"
        v-if="!showText" 
        @onClick="togglePanel"/>

      <CustomButton :icon="{ icon: 'note-sticky', style: 'far' }"
        :type="ButtonType.OUTLINED"
        text="Notes"
        v-if="showText"
        @onClick="togglePanel"/>
      <span v-if="localCaseNoteCount > 0" :class="showText || onSidebar ? 'note-badge-has-text' : 'note-badge'">
        {{ localCaseNoteCount > 99 ? '99+' : localCaseNoteCount }}
      </span>
    </div>

    <CaseNoteDialog :visible="showNoteDialog"
      :case-identifier="caseModel.identifier"
      :timepoint-options="pumpTimepointOptions"
      @onClose="showNoteDialog = false"
      @onSaved="handleNoteSaved"/>

    <CaseNotesPanel :visible="showNotesPanel"
      :case-identifier="caseModel.identifier"
      :case-label="caseModel.label"
      :timepoint-options="pumpTimepointOptions"
      :show-pin-button="true"
      @onClose="showNotesPanel = false"
      @onAddNote="showNoteDialog = true"
      @onPinChange="emit('onPinChange', $event)"/>
  </div>
</template>

<style scoped lang="scss">
@use '../../../assets/_variables.module.scss' as *;

.note-button-root {
  display: inline-flex;
  align-items: center;
}

.note-btn-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.note-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 12px;
  height: 12px;
  padding: 0 2px;
  border-radius: 8px;
  background: var(--p-primary-color, #{$primary-color});
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
  text-align: center;
  pointer-events: none;
  z-index: 1;
}

.note-badge-has-text {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 2px;
  border-radius: 8px;
  background: var(--p-primary-color, #{$primary-color});
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
  pointer-events: none;
  z-index: 1;
}

:deep(.icon-only) {
  padding: unset;
  min-width: unset;
}
.on-sidebar {
  :deep(.icon) {
    width: 24px;
    height: 24px;
  }
}
</style>
